/**
 * YouTube how-to videos per tool (channel: @freedailypro).
 * Value is the YouTube video id from watch?v=...
 * Plays via official youtube.com/embed so views count on the channel.
 */
export type ToolVideo = {
  videoId: string;
  title: string;
  /** Real YouTube upload date (YYYY-MM-DD), where known. Optional --
   * older entries predate this field; consumers fall back to a default
   * (see ToolPage's VideoObject) rather than guessing per video. */
  uploadDate?: string;
};

export const TOOL_VIDEOS: Record<string, ToolVideo> = {
  "social-media-formatter": {
    videoId: "wGC6PHLbfug",
    title: "How to Resize Images for Instagram Facebook YouTube Free Online",
  },
  // Audio
  "audio-merger": {
    videoId: "4p7PyYX44IQ",
    title: "How to Merge Audio Files Online Free – Combine Multiple Clips",
  },
  "audio-scrambler": {
    videoId: "eX69XU9oBq8",
    title: "How to Disguise Voice Online Free – Pitch Shift Audio Scrambler",
  },
  "silence-remover": {
    videoId: "z5CAZByzcc4",
    title: "How to Remove Silence from Audio Online Free – Auto Trim Quiet Parts",
  },
  "audio-trimmer": {
    videoId: "pc4DDk1feTg",
    title: "How to Trim Audio Online Free – Cut Start & End Points",
  },
  "audio-speed-changer": {
    videoId: "GtmKMHN9lrs",
    title: "How to Change Audio Speed Online Free – No Pitch Change",
    uploadDate: "2026-09-22",
  },
  "noise-remover": {
    videoId: "oq6x3PDBaP8",
    title: "How to Remove Background Noise from Audio Online Free",
    uploadDate: "2026-09-22",
  },
  "audio-comparer": {
    videoId: "KZVe2ILAUD4",
    title: "How to Compare Two Audio Files Online Free – Similarity Percentage",
    uploadDate: "2026-09-23",
  },
  "audio-converter": {
    videoId: "Ux4d3tMWQx4",
    title: "How to Convert Audio Files Online Free – MP3 WAV OGG M4A FLAC",
    uploadDate: "2026-09-23",
  },
  "audio-compressor": {
    videoId: "PNMa1yxUO7A",
    title: "How to Compress Audio Files Online Free – Reduce Size by Bitrate",
  },
  "audio-normalizer": {
    videoId: "WG-hQcqm-as",
    title: "How to Normalize Audio Volume Online Free – Even Out Loud & Quiet Parts",
    uploadDate: "2026-09-22",
  },
  // 3D
  "mesh-3d-converter": {
    videoId: "VmKe3Isdqh8",
    title: "How to Convert 3D mesh Files from STL, OBJ, PLY to GLB Free, online & no-login",
    uploadDate: "2026-09-23",
  },
  "stl-ascii-binary-converter": {
    videoId: "oF-8Cve25zc",
    title: "How to Convert STL Files ASCII to Binary Online Free",
    uploadDate: "2026-09-23",
  },
  "mesh-3d-viewer": {
    videoId: "ajU8DQk_IcQ",
    title: "How to View & Inspect 3D Mesh Files STL OBJ PLY Online Free",
    uploadDate: "2026-09-22",
  },
  // Design & CSS
  "logo-maker": {
    videoId: "KRoAYZz2kOs",
    title: "How to Make a Logo Free Online No Signup",
  },
  "font-pairing-tool": {
    videoId: "veh-VoRz4ZU",
    title: "How to Find Perfect Font Pairings Free Online",
  },
  "box-shadow-generator": {
    videoId: "A1SRVXhvtfE",
    title: "How to Generate CSS Box Shadow Free Online Live Preview",
  },
  "favicon-generator": {
    videoId: "EJuIqMKqCMs",
    title: "How to Generate Favicon from Image Free Online",
    uploadDate: "2026-09-22",
  },
  "clip-path-generator": {
    videoId: "Eby6qlJTJZU",
    title: "How to Generate CSS Clip-Path Shapes Free Online",
  },
  "color-palette": {
    videoId: "QYe_8G_2wqs",
    title: "How to Generate Color Palettes Free Online Harmony Modes",
    uploadDate: "2026-09-23",
  },
  "color-converter": {
    videoId: "Kuyt4omoYSg",
    title: "How to Convert HEX RGB HSL Colors Free Online Instantly",
    uploadDate: "2026-09-22",
  },
  "contrast-checker": {
    videoId: "PQPgG-NnfcM",
    title: "How to Check Color Contrast WCAG AA AAA Free Online",
    uploadDate: "2026-09-23",
  },
  "border-radius-generator": {
    videoId: "qiZ2M9QBi7I",
    title: "How to Generate CSS Border Radius Free Online Live Preview",
  },
  "business-card": {
    videoId: "Nx-N-fj2cBo",
    title: "How to Design a Business Card Online Free – Download PNG Instantly",
    uploadDate: "2026-09-23",
  },
  // Developer Utilities
  "url-encoder": {
    videoId: "8FTtgAMeQ0o",
    title: "How to Encode Decode URL Free Online Query Strings",
    uploadDate: "2026-09-23",
  },
  "uuid-generator": {
    videoId: "CuyJw3t7968",
    title: "How to Generate UUID Free Online Random Unique Keys",
    uploadDate: "2026-09-23",
  },
  "timestamp-converter": {
    videoId: "teolDVEdr00",
    title: "How to Convert Unix Timestamp to Date Free Online",
  },
  "http-status-reference": {
    videoId: "DBbL5oDIYlU",
    title: "How to Look Up Any HTTP Status Code Meaning Free",
  },
  "html-entity-converter": {
    videoId: "eHapvRmyxdk",
    title: "How to Encode Decode HTML Entities Free Online",
    uploadDate: "2026-09-23",
  },
  "markdown-html-converter": {
    videoId: "CD-b3DUxTYc",
    title: "How to Convert Markdown to HTML & HTML to Markdown Free",
    uploadDate: "2026-09-23",
  },
  "cron-builder": {
    videoId: "wB-eCjP3maU",
    title: "How to Build Cron Expressions with Plain English Free",
    uploadDate: "2026-09-23",
  },
  "binary-ascii-converter": {
    videoId: "qyFwDp8WN9A",
    title: "How to Convert Text to Binary Hex ASCII Online Free",
  },
  "base64": {
    videoId: "ed_mQLhJbFI",
    title: "How to Encode & Decode Base64 Online Free No Signup",
    uploadDate: "2026-09-22",
  },
  // Converters
  "unit-converter": {
    videoId: "i01IkvYUEVo",
    title: "How to Convert Units Free Online Length Weight Temperature",
    uploadDate: "2026-09-23",
  },
  "timezone-converter": {
    videoId: "tVKF3qXDDsU",
    title: "How to Convert Time Zones Free Online Instantly",
  },
  "roman-numeral-converter": {
    videoId: "Jrmgcqn_jJw",
    title: "How to Convert Numbers to Roman Numerals Free Online",
    uploadDate: "2026-09-22",
  },
  "file-size-converter": {
    videoId: "6w3VEt_OhdE",
    title: "How to Convert File Size Units Bytes KB MB GB TB Free",
    uploadDate: "2026-09-23",
  },
  "csv-converter": {
    videoId: "M9Rssk9Dl3o",
    title: "How to Convert Excel to CSV and CSV to Excel Free",
    uploadDate: "2026-09-22",
  },
  "currency-converter": {
    videoId: "vNEXKbjYyV0",
    title: "How to Convert Currencies with Daily Exchange Rates Free",
  },
  // Calculators
  "tip-calculator": {
    videoId: "HbvcuUiGoVg",
    title: "How to Calculate Tip and Split Bill Free Online",
    uploadDate: "2026-09-23",
  },
  "random-number-generator": {
    videoId: "buvkdsqCW5w",
    title: "How to Generate Random Numbers Free Online No Duplicates",
    uploadDate: "2026-09-22",
  },
  "recipe-scaler": {
    videoId: "VO1t_Lh3GRc",
    title: "How to Scale a Recipe Up or Down Free Online",
    uploadDate: "2026-09-23",
  },
  "percentage-calculator": {
    videoId: "7JpF4u4BMl4",
    title: "How to Calculate Percentage of a Number & % Change Free",
    uploadDate: "2026-09-23",
  },
  "mortgage-calculator": {
    videoId: "p5KPFr4KYm0",
    title: "How to Calculate Mortgage Payments Free Online No Signup",
    uploadDate: "2026-09-23",
  },
  "job-experience-calculator": {
    videoId: "FtITWJlgVKM",
    title: "How to Calculate Total Work Experience in Years Months Days Free",
    uploadDate: "2026-09-23",
  },
  "age-calculator": {
    videoId: "-Bx_BtrzFy0",
    title: "How to Calculate Exact Age from Birth Date Free Online",
    uploadDate: "2026-09-23",
  },
  "gpa-calculator": {
    videoId: "jp3UFGlj77M",
    title: "How to Calculate Your GPA Free Online from Courses & Credits",
  },
  "college-529-calculator": {
    videoId: "R7KsDhi6cp8",
    title: "How to Calculate Monthly 529 College Savings Free Online",
    uploadDate: "2026-09-22",
  },
  "college-cost-calculator": {
    videoId: "ic0HTrpsHc8",
    title: "How to Calculate Total College Cost Net of Aid Free Online",
  },
  // Documents & Scanning
  "document-scanner": {
    videoId: "fILxNjJP3ZY",
    title: "How to Scan Documents from Photos Free Online",
    uploadDate: "2026-09-22",
  },
  "government-forms-hub": {
    videoId: "p33ZFOO5OOI",
    title: "How to Get Official W-4, W-9, 1099-NEC Forms Free Online",
  },
  // Business & Legal Documents
  "bill-of-sale-generator": {
    videoId: "Z7VI6WZvueE",
    title: "How to Create a Bill of Sale Online Free – Download Word or PDF",
    uploadDate: "2026-09-23",
  },
  "contract-templates": {
    videoId: "rRqb0AP3flI",
    title: "How to Create a Service Agreement Contract Online Free – Word or PDF",
    uploadDate: "2026-09-23",
  },
  "employment-offer-letter": {
    videoId: "uIwIFt2SxqU",
    title: "How to Create an Employment Offer Letter Online Free – Internship or Permanent",
  },
  "expense-tracker": {
    videoId: "4Wn1vw-Rl9w",
    title: "How to Track Expenses Online Free – Budget & Category Breakdown",
  },
  "lease-agreement-generator": {
    videoId: "j7_PbtuSfkU",
    title: "How to Create a Residential Lease Agreement Free Online",
  },
  "invoice-generator": {
    videoId: "9XjEtjnQ9YY",
    title: "How to Create Professional Invoices Free Online in Seconds",
  },
  "power-of-attorney-generator": {
    videoId: "CalVXZtvIFA",
    title: "How to Create a Power of Attorney Document Free Online",
    uploadDate: "2026-09-23",
  },
  "nda-generator": {
    videoId: "AQQ3YmjiVbc",
    title: "How to Create a Mutual NDA Agreement Free Online",
  },
  "promissory-note-generator": {
    videoId: "e_M3W3wr66I",
    title: "How to Create a Promissory Note for a Personal Loan Free Online",
    uploadDate: "2026-09-22",
  },
  "resume-builder": {
    videoId: "tqvSUfYwPQE",
    title: "How to Create an ATS-Friendly Resume Free Online",
    uploadDate: "2026-09-22",
  },
  "receipt-maker": {
    videoId: "ZcIMsQLhVKY",
    title: "How to Make a Professional Receipt Free Online PDF",
  },
  // Emoji & Unicode
  "emoji-picker": {
    videoId: "HMhO1yS49Zs",
    title: "How to Search & Copy Emoji Free Online | FreeDailyPro.com",
  },
  "emoji-unicode-converter": {
    videoId: "WmGKI6vwXTo",
    title: "How to Convert Emoji to Unicode & HTML Free Online",
    uploadDate: "2026-09-22",
  },
  // PDF
  "add-page-numbers": {
    videoId: "Ln7FMyj0x20",
    title: "How to Add Page Numbers to PDF Free Online | FreeDailyPro.com",
  },
  // Budget & Planning
  "monthly-budget-planner": {
    videoId: "gPZQHr_xRAc",
    title: "How to Track Monthly Income & Expenses Free Online | FreeDailyPro.com",
  },
  "shopping-list-planner": {
    videoId: "E7JOrJoQAm8",
    title: "How to Make a Shopping List with Budget Tracker Free Online",
    uploadDate: "2026-09-23",
  },
  "travel-budget-planner": {
    videoId: "LJVvZX2ETD8",
    title: "How to Plan Travel Budget Free Online – Trip Cost Calculator",
    uploadDate: "2026-09-23",
  },
  // Screenshots & Capture
  "screenshot-tool": {
    videoId: "lBBMlLTJYMw",
    title: "How to Capture & Annotate Screenshots Free Online",
    uploadDate: "2026-09-22",
  },
  "camera-capture": {
    videoId: "qMZFDYLCLfg",
    title: "How to Capture Webcam Photo Online Free | FreeDailyPro.com",
  },
  "voice-recorder": {
    videoId: "JhCXL31yaIQ",
    title: "How to Record Voice Online Free | FreeDailyPro.com",
  },
  // Image Tools
  "yt-thumbnail": {
    videoId: "lDBgQDbgHmY",
    title: "How to Download YouTube Thumbnails Free Online | FreeDailyPro.com",
  },
  "bulk-image": {
    videoId: "KcN1A1BAUqk",
    title: "How to Batch Process Multiple Images Free Online",
    uploadDate: "2026-09-22",
  },
  "collage-maker": {
    videoId: "ScfAdTfCIdE",
    title: "How to Make a Photo Collage Free Online | FreeDailyPro.com",
  },
  "color-picker-image": {
    videoId: "njX53TSw5mA",
    title: "How to Pick Any Color from an Image Free Online | FreeDailyPro.com",
  },
  "compress-image": {
    videoId: "cr7KoCPKvCg",
    title: "How to Compress Images Free Online with a Guaranteed Size Reduction | FreeDailyPro.com",
  },
  "convert-image": {
    videoId: "vHFSmxgaxAU",
    title: "How to Convert Image Formats JPG PNG WebP GIF Free Online",
    uploadDate: "2026-09-22",
  },
  "image-filters": {
    videoId: "dU_eP-19CKk",
    title: "How to Add Filters & Effects to Images Free Online | FreeDailyPro.com",
  },
  "image-to-svg": {
    videoId: "6AWOeiibaZk",
    title: "How to Convert Image to SVG Free Online | FreeDailyPro.com",
  },
  "meme-generator": {
    videoId: "iWPx1v6eR_Y",
    title: "How to Make Memes Free Online with Top & Bottom Text | FreeDailyPro.com",
  },
  "passport-photo": {
    videoId: "Q7Sx8-jE-w4",
    title: "How to Make Passport & Visa Photos Free Online",
    uploadDate: "2026-09-22",
  },
  "flip-rotate-image": {
    videoId: "U7b1g2XugT8",
    title: "How to Flip & Rotate Images Free Online | FreeDailyPro.com",
  },
  "sprite-sheet-tool": {
    videoId: "zmLP5t8sXPo",
    title: "How to Make & Split Sprite Sheets Online Free No Signup",
    uploadDate: "2026-09-22",
  },
  // Design
  "invitation-card-maker": {
    videoId: "CHqcfo6aFyo",
    title: "How to Make Invitation Cards Free Online | FreeDailyPro.com",
  },
  "mockup-generator": {
    videoId: "eLgHCBuT9Pk",
    title: "How to Create Device Mockups Free Online No Signup",
    uploadDate: "2026-09-22",
  },
  "startup-name-generator": {
    videoId: "QYFm6WXAeO0",
    title: "How to Generate Startup Name Ideas Free Online | FreeDailyPro.com",
  },
  "gradient-generator": {
    videoId: "nZ3ZOrWL5pQ",
    title: "How to Create CSS Gradients Free Online",
    uploadDate: "2026-09-23",
  },
  // Calculators (added this session -- see git log for the date)
  "vat-calculator": {
    videoId: "uyIXIa0bLoQ",
    title: "How to Calculate VAT Online Free \u2013 UK EU & Global Rates | FreeDailyPro.com",
  },
  "bmi-calculator": {
    videoId: "SogofBA7ENk",
    title: "How to Calculate BMI & Daily Calories Free Online",
    uploadDate: "2026-09-22",
  },
  "compound-interest": {
    videoId: "JTzRc7TqI8o",
    title: "How to Calculate Compound Interest Free Online | FreeDailyPro.com",
  },
  "discount-calculator": {
    videoId: "Cdv1ekk0Sis",
    title: "How to Calculate Discount & Sale Price Free Online | FreeDailyPro.com",
  },
  "fire-calculator": {
    videoId: "M5_7LXMIGtE",
    title: "How to Calculate Your FIRE Number & Years to Financial Independence",
    uploadDate: "2026-09-22",
  },
  "loan-calculator": {
    videoId: "ar1i9hJwVXQ",
    title: "How to Calculate Loan EMI & Monthly Payment Free Online | FreeDailyPro.com",
  },
  "paycheck-calculator": {
    videoId: "jNpP2-R9-eM",
    title: "How to Calculate Take-Home Pay & Paycheck After Taxes Free",
    uploadDate: "2026-09-22",
  },
  "profit-margin-calculator": {
    videoId: "zSn4ypgmorc",
    title: "How to Calculate Profit Margin & Markup Free Online | FreeDailyPro.com",
  },
  "rmd-calculator": {
    videoId: "TAmWl14zasw",
    title: "How to Calculate Required Minimum Distribution (RMD) Free Online | FreeDailyPro.com",
  },
  "sip-calculator": {
    videoId: "SlstGKCWhhE",
    title: "How to Calculate SIP Future Value Free Online",
    uploadDate: "2026-09-23",
  },
  "social-security-calculator": {
    videoId: "kaQQXB72ZOE",
    title: "How to Estimate Social Security Benefits Free Online | FreeDailyPro.com",
  },
  "student-loan-calculator": {
    videoId: "rkTFl7HrnCk",
    title: "How to Calculate Student Loan Repayment Free Online | FreeDailyPro.com",
  },
  "break-even-calculator": {
    videoId: "ted-3WI8kPo",
    title: "How to Calculate Break-Even Point Free Online | FreeDailyPro.com",
  },
  "401k-calculator": {
    videoId: "sE03pQnC9Sc",
    title: "How to Use 401(k) Calculator Free Online | FreeDailyPro.com",
  },
  "marketplace-fee-calculator": {
    videoId: "Yk1ivqIkHGc",
    title: "How to Calculate Marketplace Fees & Net Profit Free | FreeDailyPro.com",
  },
  // Business & Planning
  "event-budget-planner": {
    videoId: "r5NlRghvFVk",
    title: "How to Plan Event Budget & Track Spending Free | FreeDailyPro.com",
  },
  "product-description-writer": {
    videoId: "rNClwJySf3c",
    title: "How to Write Product Descriptions Fast Free | FreeDailyPro.com",
  },
  "shipping-cost-estimator": {
    videoId: "3tZD_qTh0Sw",
    title: "How to Estimate Shipping Costs Free Online",
    uploadDate: "2026-09-22",
  },
  "event-checklist-planner": {
    videoId: "mCxI8Q6kBpM",
    title: "How to Create Event Checklist & Timeline Free | FreeDailyPro.com",
  },
  "event-day-schedule": {
    videoId: "O1dOYsmn8so",
    title: "How to Build Event Day-Of Schedule Free | FreeDailyPro.com",
  },
  "invention-disclosure-worksheet": {
    videoId: "koB11wbTS60",
    title: "How to Create Invention Disclosure Worksheet Free | FreeDailyPro.com",
  },
  "eviction-notice-generator": {
    videoId: "HDr9TKm5EQI",
    title: "How to Create an Eviction Notice Online Free \u2013 Pay Rent or Quit | FreeDailyPro.com",
  },
  // Developer Utilities
  "ocr-tool": {
    videoId: "Q1WWHH9P0-8",
    title: "How to Extract Text from Image Free OCR | FreeDailyPro.com",
  },
  "zip-unzip": {
    videoId: "EdMCrV1uUUo",
    title: "How to ZIP & UnZIP Files Free Online | FreeDailyPro.com",
  },
  "json-formatter": {
    videoId: "8qndJoI2HFQ",
    title: "How to Format Validate & Beautify JSON YAML Free | FreeDailyPro.com",
  },
  "css-js-minifier": {
    videoId: "w-nM8zPJ-iQ",
    title: "How to Minify CSS and JavaScript Free Online | FreeDailyPro.com",
  },
  "ascii-art-generator": {
    videoId: "knX3VOJhhuo",
    title: "How to Create ASCII Art from Text Free Online",
    uploadDate: "2026-09-22",
  },
  // Documents & PDF
  // book-writing-toolbox: deliberately no entry -- the previous video
  // (id 6TNCCPgFhB4) demoed the old upload-and-merge workflow, which
  // no longer exists on the page now that Book Writing Toolbox is a
  // real writing editor. Showing that video would demo a product that
  // isn't there anymore. Removed rather than left pointing at the
  // wrong thing; add a new one back once a real demo of the new
  // editor exists.
  "catalog-maker": {
    videoId: "HK72jnNM8yA",
    title: "How to Make a Product Catalog PDF Free Online",
    uploadDate: "2026-09-23",
  },
  "compare-pdf": {
    videoId: "f-QHfStX3Po",
    title: "How to Compare Two PDF Files Free Online | FreeDailyPro.com",
  },
  "compress-pdf": {
    videoId: "XZDDliVHrLY",
    title: "How to Compress PDF File Size Free Online",
    uploadDate: "2026-09-22",
  },
  "crop-pdf": {
    videoId: "IQB9dwWbjEc",
    title: "How to Crop PDF Pages Free Online",
    uploadDate: "2026-09-22",
  },
  "excel-to-pdf": {
    videoId: "uHlSx5_uZ6o",
    title: "How to Convert Excel Spreadsheet to Readable PDF Table Free",
    uploadDate: "2026-09-22",
  },
  "extract-pdf": {
    videoId: "XZ3ghDwxXf4",
    title: "How to Extract Specific Pages or Text from PDF Free Online",
    uploadDate: "2026-09-23",
  },
  "fillable-forms": {
    videoId: "spu6o1Ou7Oc",
    title: "How to Fill PDF Form Fields Online Free – Text Checkbox Dropdown",
    uploadDate: "2026-09-23",
  },
  "html-to-pdf": {
    videoId: "JqeVfUR3J-g",
    title: "How to Convert HTML to PDF Free Online – Paste Code & Download",
    uploadDate: "2026-09-22",
  },
  "image-to-pdf": {
    videoId: "VeWJoEkv8Kk",
    title: "How to Convert Images to PDF Free Online \u2013 JPG PNG Multi-Image | FreeDailyPro.com",
  },
  "magazine-maker": {
    videoId: "AEV2y69z1t0",
    title: "How to Make a Magazine PDF Free Online \u2013 Cover, TOC & Articles | FreeDailyPro.com",
  },
  "merge-pdf": {
    videoId: "0FjbjU4eDVw",
    title: "How to Merge PDF Files Free Online \u2013 Combine Multiple PDFs | FreeDailyPro.com",
  },
  // Image Tools
  "resize-crop": {
    videoId: "RLVYl39EfcQ",
    title: "How to Resize & Crop Images Free Online",
    uploadDate: "2026-09-23",
  },
  "webp-converter": {
    videoId: "Xm4c2TGRZow",
    title: "How to Convert Images to WebP Free Online",
    uploadDate: "2026-09-23",
  },
  // Design
  "banner-sign-maker": {
    videoId: "j5Qcc08JSpA",
    title: "How to Make Banners & Signs Free Online",
    uploadDate: "2026-09-23",
  },
  "brochure-flyer-maker": {
    videoId: "xQdq45H9Vnc",
    title: "How to Make Brochures & Flyers Free Online | FreeDailyPro.com",
  },
  "menu-card-maker": {
    videoId: "a5lyb_NP-QQ",
    title: "How to Make Restaurant Menu Cards Free Online | FreeDailyPro.com",
  },
  // Added this session -- YouTube channel published 30 new videos;
  // 29 are genuinely new mappings, vat-calculator already existed.
  "pdf-to-word": {
    videoId: "K61zcnXg6sU",
    title: "How to Convert PDF to Word Free Online – Editable DOCX",
    uploadDate: "2026-09-23",
  },
  "pdf-watermark": {
    videoId: "g65Ab2hXV-I",
    title: "How to Add Watermark to PDF Free Online \u2013 Text Stamp All Pages | FreeDailyPro.com",
  },
  "redact-pdf": {
    videoId: "PsNyMsdgdqw",
    title: "How to Redact PDF Free Online \u2013 True Permanent Removal | FreeDailyPro.com",
  },
  "repair-pdf": {
    videoId: "T7f0XAhH6Oo",
    title: "How to Repair Corrupted PDF Free Online \u2013 Recover Broken Files | FreeDailyPro.com",
  },
  "pdf-to-jpg": {
    videoId: "WUw3lpbCV9E",
    title: "How to Convert PDF to JPG Free Online – High Quality Images",
    uploadDate: "2026-09-22",
  },
  "rotate-pdf": {
    videoId: "eqHPb2W0e_I",
    title: "How to Rotate PDF Pages Free Online \u2013 Fix Sideways PDFs | FreeDailyPro.com",
  },
  "sign-pdf": {
    videoId: "bP3uV1CNIwM",
    title: "How to Sign a PDF Free Online \u2013 Draw Signature & Place Fields | FreeDailyPro.com",
  },
  "cost-of-living-comparison": {
    videoId: "XcaHWWGlKCY",
    title: "How to Compare Cost of Living Between Two Cities Free Online | FreeDailyPro.com",
  },
  "moving-cost-estimator": {
    videoId: "mbsNH-Hh85M",
    title: "How to Estimate Moving Costs by Home Size & Distance Free Online | FreeDailyPro.com",
  },
  "relocation-checklist-planner": {
    videoId: "pCbBo-fCQAM",
    title: "How to Plan Your Move with a Free Relocation Checklist & Timeline | FreeDailyPro.com",
  },
  "email-extractor": {
    videoId: "kzsTTF8gsvI",
    title: "How to Extract Email Addresses from Any Text Free Online",
    uploadDate: "2026-09-22",
  },
  "meta-tag-gen": {
    videoId: "6dX4YGIZEdc",
    title: "How to Generate SEO Meta Tags Free Online | FreeDailyPro.com",
  },
  "keyword-density": {
    videoId: "MNucDZebBDI",
    title: "How to Check Keyword Density in Your Content Free Online | FreeDailyPro.com",
  },
  "robots-sitemap": {
    videoId: "F5J-eaNCJnQ",
    title: "How to Generate Robots.txt & XML Sitemap Free Online | FreeDailyPro.com",
  },
  "schema-generator": {
    videoId: "X7BBN4vDtHs",
    title: "How to Generate Schema Markup JSON-LD Free Online | FreeDailyPro.com",
  },
  "url-extractor": {
    videoId: "pbC7N9Jp7r0",
    title: "How to Extract All URLs from Text or HTML Free Online | FreeDailyPro.com",
  },
  "diff-checker": {
    videoId: "Hty9FvUCOS0",
    title: "How to Compare Two Texts or Code Side by Side Free Online | FreeDailyPro.com",
  },
  "hash-generator": {
    videoId: "7vHVmGVIeWA",
    title: "How to Generate SHA-256 & MD5 Hashes Free Online",
    uploadDate: "2026-09-23",
  },
  "jwt-decoder": {
    videoId: "OhUnKjmiP48",
    title: "How to Decode JWT Tokens Free Online",
    uploadDate: "2026-09-22",
  },
  "password-generator": {
    videoId: "LrkYLI-EnRs",
    title: "How to Generate Strong Secure Passwords Free Online | FreeDailyPro.com",
  },
  "password-strength": {
    videoId: "qrmfDS4gqlA",
    title: "How to Generate & Check Password Strength Free Online",
    uploadDate: "2026-09-22",
  },
  "avatar-builder": {
    videoId: "I4L9KC-9wZo",
    title: "How to Build a Custom Avatar Online Free – No Photo Needed",
    uploadDate: "2026-09-23",
  },
  "gst-calculator": {
    videoId: "zOgKD95cdng",
    title: "How to Calculate GST Online Free \u2013 CGST SGST India | FreeDailyPro.com",
  },
  "india-income-tax": {
    videoId: "40kGdVGmmwY",
    title: "How to Compare India Old vs New Tax Regime Free | FreeDailyPro.com",
  },
  "income-tax-calculator": {
    videoId: "UvcrEE6Swwc",
    title: "How to Calculate US Income Tax Online Free – Federal State FICA",
    uploadDate: "2026-09-22",
  },
  "sales-tax-calculator": {
    videoId: "4qfHuNiEn6Y",
    title: "How to Calculate US Sales Tax Free Online | FreeDailyPro.com",
  },
  "self-employment-tax": {
    videoId: "ZTtLN2qo5NA",
    title: "How to Calculate Self-Employment Tax Free Online | FreeDailyPro.com",
  },
  "uk-tax-calculator": {
    videoId: "ID2y6oBsqgw",
    title: "How to Calculate UK Income Tax & National Insurance Free | FreeDailyPro.com",
  },
  "state-tax-calculator": {
    videoId: "uRiqzi4ARPY",
    title: "How to Estimate State Income Tax by State Free | FreeDailyPro.com",
  },
  // Added this session -- 30 more new videos from the channel; 27 are
  // genuine new mappings (resize-crop already has its own video with a
  // different videoId, so the new "resize for social media" video isn't
  // used here to avoid silently replacing an existing embed without
  // confirming that's actually wanted).
  "citation-generator": {
    videoId: "kIdCSeCYAzM",
    title: "How to Generate APA MLA Chicago Harvard Citations Free",
    uploadDate: "2026-09-23",
  },
  "reading-level-checker": {
    videoId: "ZMZUaB_LLJ4",
    title: "How to Check Reading Level & Flesch Score Free Online",
    uploadDate: "2026-09-23",
  },
  "text-similarity-checker": {
    videoId: "uzoT0aEeOys",
    title: "How to Check Text Similarity Overlapping Phrases Free Online",
    uploadDate: "2026-09-23",
  },
  "text-reverser": {
    videoId: "y-EuITaTR0A",
    title: "How to Reverse Text Remove Duplicate Words Lines Free Online",
    uploadDate: "2026-09-23",
  },
  "text-sorter": {
    videoId: "3RBVLZEmo8g",
    title: "How to Sort Text Lines Alphabetically or by Length Free Online",
    uploadDate: "2026-09-23",
  },
  "travel-countdown-planner": {
    videoId: "-f1KN3d570w",
    title: "How to Plan Pre-Trip Tasks & Countdown Days Until Departure Free",
    uploadDate: "2026-09-23",
  },
  "word-counter": {
    videoId: "AVCllh6Nih8",
    title: "How to Count Words, Characters & Reading Time Online Free",
    uploadDate: "2026-09-23",
  },
  "travel-itinerary-planner": {
    videoId: "PMZ_Hf1vUP8",
    title: "How to Build a Day-by-Day Travel Itinerary Free Online",
    uploadDate: "2026-09-23",
  },
  "travel-packing-list": {
    videoId: "De6dCE_A3f4",
    title: "How to Make a Travel Packing Checklist Free Online",
    uploadDate: "2026-09-23",
  },
  "compress-video": {
    videoId: "j2eUOdBo3bk",
    title: "How to Compress Video Online Free – Reduce File Size",
    uploadDate: "2026-09-23",
  },
  "video-to-gif": {
    videoId: "P9s4XBNSB10",
    title: "How to Convert Video to GIF Online Free",
    uploadDate: "2026-09-23",
  },
  "trim-video": {
    videoId: "0MNpfjMKweo",
    title: "How to Trim & Cut Video Online Free",
    uploadDate: "2026-09-23",
  },
  "convert-video": {
    videoId: "zqpLPhmyj9E",
    title: "How to Convert Video Format Online Free – MP4 WebM MOV",
    uploadDate: "2026-09-23",
  },
  "merge-video": {
    videoId: "-HjR7V5ceA0",
    title: "How to Merge Videos Online Free – Combine Multiple Clips",
    uploadDate: "2026-09-23",
  },
  "screen-recorder": {
    videoId: "DEwAYXXSJ-c",
    title: "How to Record Your Screen Online Free – Browser Screen Recorder",
    uploadDate: "2026-09-23",
  },
  "thumbnail-gen": {
    videoId: "wGXvbaUKqQI",
    title: "How to Generate Video Thumbnails Free Online \u2013 Frame Capture Tool | FreeDailyPro.com",
  },
  "caption-generator": {
    videoId: "OGI6yqaI1oU",
    title: "How to Generate AI Social Media Captions Free",
    uploadDate: "2026-09-23",
  },
  "case-converter": {
    videoId: "qv3ivSaFGY8",
    title: "How to Convert Text Case Online Free",
    uploadDate: "2026-09-23",
  },
  "fancy-font-generator": {
    videoId: "H7MsRQ4kJlo",
    title: "How to Generate Fancy Unicode Fonts for Bios Free",
    uploadDate: "2026-09-23",
  },
  "regex-tester": {
    videoId: "MbNCq9Bat5A",
    title: "How to Test Regex Online Free – Live Match Highlight",
    uploadDate: "2026-09-23",
  },
  "grammar-checker": {
    videoId: "bMWHXqeygUg",
    title: "How to Check Grammar and Spelling Free Online",
    uploadDate: "2026-09-23",
  },
  "hashtag-generator": {
    videoId: "x7-e6_ut-Sk",
    title: "How to Generate Hashtags for Social Media Free Online",
    uploadDate: "2026-09-23",
  },
  "body-fat-calculator": {
    videoId: "gDEpxN9HkiA",
    title: "How to Calculate Body Fat Percentage Free Online",
    uploadDate: "2026-09-23",
  },
  "ideal-weight-calculator": {
    videoId: "z1CXmb95Z94",
    title: "How to Calculate Ideal Body Weight Free Online",
    uploadDate: "2026-09-23",
  },
  "pregnancy-due-date": {
    videoId: "fapbkFRo_ns",
    title: "How to Calculate Pregnancy Due Date Free Online",
    uploadDate: "2026-09-23",
  },
  "water-intake-calculator": {
    videoId: "qyFt9XvLAA4",
    title: "How to Calculate Daily Water Intake Free Online",
    uploadDate: "2026-09-23",
  },
  "inventory-tracker": {
    videoId: "3T-BdlZM2bI",
    title: "How to Track Home Inventory with Low Stock Alerts Free Online",
    uploadDate: "2026-09-23",
  },

  // Added: tools that had no video before (newest uploads on @freedailypro)
  "pdf-to-excel": {
    videoId: "dGgPVkXxdII",
    title: "How to Convert PDF Tables to Excel Free Online",
    uploadDate: "2026-09-24",
  },
  "pdf-password": {
    videoId: "qBJKvkcI298",
    title: "How to Password Protect a PDF Free Online – Encrypt PDF",
    uploadDate: "2026-09-24",
  },
  "organize-pdf": {
    videoId: "TZTL_wd-NJY",
    title: "How to Reorder or Remove PDF Pages Free Online with Previews",
    uploadDate: "2026-09-23",
  },
  "find-replace": {
    videoId: "ZRbu9e3kDO4",
    title: "How to Find and Replace Text with Regex Free Online",
    uploadDate: "2026-09-23",
  },
  "paper-format-checker": {
    videoId: "f4VPRP4U4iM",
    title: "How to Check APA MLA Chicago Paper Format Free Online",
    uploadDate: "2026-09-23",
  },
  "lorem-ipsum": {
    videoId: "HmIveDK3fiU",
    title: "How to Generate Lorem Ipsum Placeholder Text Free Online",
    uploadDate: "2026-09-23",
  },
  "invisible-text-generator": {
    videoId: "AIYW2os7qxE",
    title: "How to Generate Invisible Text for Blank Bios & Messages Free",
    uploadDate: "2026-09-23",
  },
  "user-agent-parser": {
    videoId: "-iWoAjyeKOY",
    title: "How to Parse User-Agent Strings Free Online – Browser OS Device",
    uploadDate: "2026-09-23",
  },
  "text-paraphraser": {
    videoId: "4NQaC_Th79E",
    title: "How to Paraphrase & Rewrite Text Free Online",
    uploadDate: "2026-09-23",
  },
  "slug-generator": {
    videoId: "SZ5roXw5GsU",
    title: "How to Generate Clean URL Slugs Free Online – SEO Friendly",
    uploadDate: "2026-09-23",
  },
  "subtitle-adder": {
    videoId: "6bjDBJzod6I",
    title: "How to Add Subtitles to a Video Free Online",
    uploadDate: "2026-09-23",
  },
  "og-preview": {
    videoId: "mC6zWTTMtv4",
    title: "How to Preview Open Graph & Twitter Cards Free Online",
    uploadDate: "2026-09-23",
  },
  "document-assembler": {
    videoId: "Lp5L3YhS8-w",
    title: "How to Combine PDF Word & Images into One Document Free",
    uploadDate: "2026-09-23",
  },
  "calorie-calculator": {
    videoId: "xbEpQlarSCk",
    title: "How to Work Out Daily Calories to Lose or Gain Weight with Calorie Calculator | FreeDailyPro",
    uploadDate: "2026-10-24",
  },
  "book-writing-toolbox": {
    videoId: "C2e9b_ROuk8",
    title: "How to Make a Complete Book from Chapters PDF Word Free Online",
    uploadDate: "2026-09-22",
  },
  // Uploaded 16 Oct 2026
  "split-pdf": {
    videoId: "0jI1pCsuiqI",
    title: "How to Split a PDF into Separate Files with Split PDF | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "word-to-pdf": {
    videoId: "cfoa2qODfW4",
    title: "How to Convert a Word Document to PDF with Word to PDF | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "powerpoint-to-pdf": {
    videoId: "rnjZAS1Vulg",
    title: "How to Convert a PowerPoint Deck to PDF with PowerPoint to PDF | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "ocr-pdf": {
    videoId: "djmvThBol8c",
    title: "How to Make a Scanned PDF Searchable with OCR PDF | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "flatten-pdf": {
    videoId: "LiZ5JgDHNnk",
    title: "How to Lock Filled Form Fields with Flatten PDF | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "pdf-editor": {
    videoId: "-pRfCJA4NVQ",
    title: "How to Highlight and Add Notes to a PDF with PDF Editor | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "remove-pdf-pages": {
    videoId: "iNfdYh_spXM",
    title: "How to Delete Blank Pages from a PDF with Remove PDF Pages | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "remove-background": {
    videoId: "N5fCqhLPM6I",
    title: "How to Remove the Background from a Photo with Remove Background | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "image-quality-enhancer": {
    videoId: "KJ9IMsA5zNM",
    title: "How to Sharpen and Upscale a Small Photo with Image Quality Enhancer | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "exif-remover": {
    videoId: "vIbIpFfk8nk",
    title: "How to Remove Location and Camera Data from a Photo with EXIF Remover | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "image-splitter": {
    videoId: "OcxzYl0KT5w",
    title: "How to Split a Panorama into Instagram Tiles with Image Splitter | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "mute-video": {
    videoId: "9jUNUwt5IFc",
    title: "How to Remove the Sound from a Video with Mute Video | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "rotate-video": {
    videoId: "3XvQdeZuFR0",
    title: "How to Fix a Sideways Video with Rotate Video | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "video-speed-changer": {
    videoId: "kJRTfIjQrFA",
    title: "How to Make a 2x Speed Video with Video Speed Changer | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "reverse-video": {
    videoId: "1hAwFEuYcB8",
    title: "How to Play a Video Backwards with Reverse Video | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "extract-audio": {
    videoId: "-_jML77XmXc",
    title: "How to Pull the Audio Out of a Video with Extract Audio | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "ringtone-maker": {
    videoId: "VEb2v517ry0",
    title: "How to Make a Ringtone from a Song with Ringtone Maker | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "volume-booster": {
    videoId: "YV065aRRkRs",
    title: "How to Make a Quiet Recording Louder with Volume Booster | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "text-cleaner": {
    videoId: "U2Se4697_dQ",
    title: "How to Clean Up Messy Pasted Text with Text Cleaner | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "number-to-words": {
    videoId: "j9mcGrtz34E",
    title: "How to Write a Number in Words for a Cheque with Number to Words | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "morse-code-translator": {
    videoId: "S3L-6aokDVE",
    title: "How to Translate Text to Morse Code and Back with Morse Code Translator | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "universal-file-converter": {
    videoId: "gkHJ4c6-d7s",
    title: "How to Convert a CSV File to Excel with Universal File Converter | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "final-grade-calculator": {
    videoId: "7TGow6x6IZM",
    title: "How to Find the Score You Need on Your Final with Final Grade Calculator | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "bmr-macro-calculator": {
    videoId: "I9hBg_vI8p4",
    title: "How to Work Out Your BMR and Daily Macros with BMR & Macro Calculator | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "qr-generator": {
    videoId: "OwklDj1Z5_w",
    title: "How to Make a QR Code for a Link with QR Code Generator | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "custom-qr": {
    videoId: "-YoQA8K6x8I",
    title: "How to Design a Branded QR Code with Custom QR Code | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "bulk-qr": {
    videoId: "ui6-i9QUCxA",
    title: "How to Make Many QR Codes at Once with Bulk QR Generator | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "qr-scanner": {
    videoId: "3QwOMlvwSaA",
    title: "How to Read a QR Code or Barcode from a Picture with QR & Barcode Scanner | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "barcode-generator": {
    videoId: "MXkKnwkHfrk",
    title: "How to Make a Product Barcode with Barcode Generator | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "user-manual-writer": {
    videoId: "5f4wXqncT0A",
    title: "How to Write a Quick-Start Manual with User Manual Writer | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "quote-generator": {
    videoId: "t9WZFj5xYhY",
    title: "How to Make a Price Quote PDF with Quote Generator | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "purchase-order-generator": {
    videoId: "QmLqFGvVtj8",
    title: "How to Make a Purchase Order PDF with Purchase Order Generator | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "packing-slip-generator": {
    videoId: "WI9iAau2Z8E",
    title: "How to Make a Packing Slip PDF with Packing Slip Generator | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "email-signature-generator": {
    videoId: "m0nIACyMfBc",
    title: "How to Make a Professional Email Signature with Email Signature Generator | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "label-sheet-maker": {
    videoId: "8Whscyaabvk",
    title: "How to Print Address Labels on Avery Sheets with Label Sheet Maker | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "brand-kit": {
    videoId: "iOTJYv1ZEY8",
    title: "How to Build a Brand Colour Palette and Kit with Brand Kit | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "sql-formatter": {
    videoId: "LCyMSuBQ4Y8",
    title: "How to Format a Messy SQL Query with SQL Formatter | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "curl-converter": {
    videoId: "C9LO2nMrsY0",
    title: "How to Convert a cURL Command to JavaScript or Python with cURL Converter | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "subnet-calculator": {
    videoId: "yhUsvA-xCz8",
    title: "How to Work Out an IPv4 Subnet with Subnet Calculator | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "chmod-calculator": {
    videoId: "Qz78lspAgE8",
    title: "How to Work Out Unix File Permissions with Chmod Calculator | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "string-escape": {
    videoId: "Di_rT-ZOj8g",
    title: "How to Escape Text for JSON, HTML or URLs with String Escape | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "site-launch-kit": {
    videoId: "xq5Y8ZOK-_Y",
    title: "How to Make robots.txt, a Sitemap and Head Tags with Site Launch Kit | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "listing-photo-pack": {
    videoId: "5SILn9VbQ_s",
    title: "How to Size Product Photos for Etsy, eBay or Amazon with Listing Photo Pack | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "seller-pricing-desk": {
    videoId: "8roVwU4qiSU",
    title: "How to Price Products After Marketplace Fees with Seller Pricing Desk | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "guest-list-tracker": {
    videoId: "5SWBaT7vTTA",
    title: "How to Track RSVPs and Plus-Ones with Guest List Tracker | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "grocery-list-planner": {
    videoId: "Q2yp7z08ZnE",
    title: "How to Make a Grocery List by Aisle with Grocery List Planner | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "paper-submission-desk": {
    videoId: "C-qX8AjoGdA",
    title: "How to Check a Paper Against a Journal's Limits with Paper Submission Desk | FreeDailyPro",
    uploadDate: "2026-10-16",
  },
  "heic-to-jpg": {
    videoId: "x4aiBcSKXbw",
    title: "How to Convert iPhone HEIC Photos to JPG with HEIC to JPG Converter | FreeDailyPro",
    uploadDate: "2026-10-24",
  },
  "business-days-calculator": {
    videoId: "9APtmuuBiCs",
    title: "How to Count Working Days Between Two Dates with Business Days Calculator",
    uploadDate: "2026-10-28",
  },
  "json-csv-converter": {
    videoId: "SHB9hErUc9M",
    title: "How to Convert JSON to CSV for Excel with JSON and CSV Converter",
    uploadDate: "2026-10-27",
  },
  "mail-merge-pdf": {
    videoId: "33PMneYgT7c",
    title: "How to Mail Merge a List into PDF Letters with Mail Merge to PDF",
    uploadDate: "2026-10-27",
  },
  "bank-statement-to-csv": {
    videoId: "0FEmFkrFsOw",
    title: "How to Convert a Bank Statement PDF to CSV or Excel with Bank Statement to CSV",
    uploadDate: "2026-10-26",
  },
  "bates-numbering": {
    videoId: "1u1qW4vfozE",
    title: "How to Add Bates Numbers to PDF Files with Bates Numbering",
    uploadDate: "2026-10-25",
  },
  "pdf-bookmarks-editor": {
    videoId: "2POndkNYCkc",
    title: "How to Rename and Fix PDF Bookmarks with PDF Bookmarks Editor",
    uploadDate: "2026-10-25",
  },
};

export function getToolVideo(toolId: string): ToolVideo | undefined {
  return TOOL_VIDEOS[toolId];
}

/**
 * On-page wording for a video title (button label, iframe title, fallback
 * heading). The real YouTube title keeps its search wording ("... Free
 * Online") and still feeds the VideoObject structured data; visible and
 * screen-reader text drops the standalone word "Free" (B-0015).
 */
export function videoDisplayTitle(title: string): string {
  return title
    .replace(/\s+Free(?=\s+Online\b)/gi, "")
    .replace(/\s+Free\b(?!-)/gi, "")
    .replace(/\bFree\s+/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}
